import React, { useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Card,
  CardContent,
  Link,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { cleanCpf, formatCpfInput } from '../utils/formatCpf';
import { customerPasswordResetService } from '../services/customerPublicApi';

const CustomerForgotPassword: React.FC = () => {
  const [cpf, setCpf] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    const normalizedCpf = cleanCpf(cpf);
    if (normalizedCpf.length !== 11) {
      setError('Informe um CPF válido.');
      return;
    }

    setLoading(true);
    try {
      const response = await customerPasswordResetService.requestCode(normalizedCpf);
      setSuccess(
        response.data.message ||
          'Se houver cadastro, enviaremos um código para o WhatsApp e/ou e-mail da sua inscrição mais recente.',
      );
      setTimeout(() => {
        navigate('/customer/reset-password', { state: { cpf: normalizedCpf } });
      }, 2500);
    } catch (requestError: unknown) {
      const axiosError = requestError as {
        response?: { status?: number; data?: { message?: string } };
        message?: string;
      };
      if (axiosError.response?.status === 429) {
        setError('Muitas tentativas. Aguarde alguns minutos e tente novamente.');
      } else if (axiosError.response?.status === 503) {
        setError(
          axiosError.response.data?.message ||
            'Não foi possível enviar o código. Tente novamente mais tarde ou fale com o suporte.',
        );
      } else {
        setError('Não foi possível processar sua solicitação. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 440 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" component="h1" gutterBottom align="center" fontWeight={700}>
              Esqueci minha senha
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Informe seu CPF. Enviaremos um código para o WhatsApp e/ou e-mail cadastrados na sua
              inscrição mais recente.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="CPF"
                value={cpf}
                onChange={(event) => setCpf(formatCpfInput(event.target.value))}
                margin="normal"
                required
                placeholder="000.000.000-00"
                inputProps={{ maxLength: 14 }}
                disabled={loading || !!success}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || !!success}
              >
                {loading ? 'Enviando...' : 'Enviar código'}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link component={RouterLink} to="/customer/login" underline="hover">
                  Voltar ao login
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default CustomerForgotPassword;
